import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  computed,
} from "@angular/core";
import { LazyLoadEvent, SharedModule } from "primeng/api";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import moment from "moment";
import { HotToastService } from "@ngneat/hot-toast";
import { DashboardService } from "src/app/dashboard/dashboard.service";
import { Data } from "./../../../../../shared-data.service";
import { JobsService } from "./../../jobs.service";
import { JobTimeTrackingService } from "./../job-time-tracking-modal/job-time-tracking.service";
import { JobsStore } from "./../../jobs.store";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import {
  PERMISSION_JOBS_TIME_TRACKING_VIEW_OTHERS,
  ROLE_ADMIN,
  ROLE_MANAGER,
  UserPermissions,
} from "src/app/services/authorization/authorization.models";
import { NgFor, NgIf } from "@angular/common";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { FormsModule } from "@angular/forms";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { MomentModule } from "ngx-moment";
import { EmptyStateComponent } from "src/app/design-system/components/empty-state/empty-state.component";

@Component({
  selector: "app-time-tracking-dashboard",
  templateUrl: "./time-tracking-dashboard.component.html",
  styleUrls: ["./time-tracking-dashboard.component.scss"],
  imports: [
    NgIf,
    BsDropdownModule,
    NgFor,
    DatePickerModule,
    FormsModule,
    TableModule,
    SharedModule,
    TooltipModule,
    MomentModule,
    EmptyStateComponent,
  ],
})
export class TimeTrackingDashboardComponent implements OnInit {
  readonly PERMISSION_JOBS_TIME_TRACKING_VIEW_OTHERS =
    PERMISSION_JOBS_TIME_TRACKING_VIEW_OTHERS;
  readonly ROLE_ADMIN = ROLE_ADMIN;
  readonly ROLE_MANAGER = ROLE_MANAGER;
  dashboardName = "timeTracking";
  timeTrackings: any[] = [];
  data: Data;
  cols: any[];
  loading: boolean = false;
  totalRecords: number = 0;
  userPermissions: UserPermissions;
  lastLazyEvent: LazyLoadEvent;

  initialFilters = {
    timeTracking: {
      personFilter: { label: "Me", value: "me" },
      timeFrameFilter: { label: "This week", value: "this_week" },
      groupByFilter: { label: "Employee", value: "employee" },
      sortOrder: 1,
      sortBy: "time_tracked",
      rows: 100,
    },
  };

  multiSortMeta = [
    { field: "userFullName", order: 1 },
    { field: "time_tracked", order: 1 },
  ];

  groupByField = computed(() => {
    const filters = this.dashboardService.filters();
    return filters[this.dashboardName].groupByFilter.value === "employee"
      ? "userFullName"
      : "company";
  });

  @ViewChild("calendar") calendar: DatePicker;
  date: any;

  constructor(
    private timetrackingService: JobTimeTrackingService,
    public jobsStore: JobsStore,
    private localStorage: LocalStorageService,
    private cdr: ChangeDetectorRef,
    public dashboardService: DashboardService,
    private jobsService: JobsService,
    private toastr: HotToastService,
  ) {}

  ngOnInit(): void {
    this.data = this.localStorage.get("data");
    this.userPermissions = this.localStorage.get("userPermissions");

    if (!this.dashboardService.filters()[this.dashboardName]) {
      this.dashboardService.filters.update((prevState = {}) => ({
        ...prevState,
        [this.dashboardName]: {
          ...this.initialFilters.timeTracking,
        },
      }));
    }
    this.cols = [
      { field: "company", header: "Name" },
      { field: "job", header: "Job" },
      { field: "start_date", header: "Start Date" },
      { field: "time_tracked", header: "Time tracked" },
    ];
    this.retrieveEntries();
  }

  retrieveEntries(event: LazyLoadEvent = null) {
    this.loading = true;
    if (event) {
      this.lastLazyEvent = event;
    }
    const {
      [this.dashboardName]: {
        personFilter: { value: personFilter },
        timeFrameFilter: { value: timeFrameFilter },
        sortBy,
        sortOrder,
      },
    } = this.dashboardService.filters();

    this.timetrackingService
      .retrieveTimeTrackingEntries(
        personFilter,
        timeFrameFilter,
        sortBy,
        sortOrder,
        this.lastLazyEvent?.first,
        this.lastLazyEvent?.rows,
      )
      .then(({ time_trackings, total_count }: any) => {
        // Flatten data by adding a top-level "userFullName" property.
        this.timeTrackings = time_trackings.map((entry) => ({
          ...entry,
          start_date: new Date(entry.start_date),
          userFullName: entry.user?.full_name,
        }));
        this.totalRecords = total_count;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  changePersonFilter(change: string | { id: number; name: string }) {
    let label: string;
    let value: any;
    if (typeof change === "object") {
      value = change.id;
      label = change.name;
    } else {
      value = change;
      if (change === "me") {
        label = "Me";
      } else if (change === "everyone") {
        label = "Everyone";
      }
    }
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState.timeTracking,
        personFilter: { label, value },
      },
    }));
    this.retrieveEntries();
  }

  changeSortByFilter(sortBy: string, sortOrder: number) {
    console.log("sortBy", sortBy);
    const {
      [this.dashboardName]: { sortOrder: currentSortOrder },
    } = this.dashboardService.filters();
    let newSortOrder = currentSortOrder === -1 ? 1 : sortOrder;
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState.timeTracking,
        sortOrder: newSortOrder,
        sortBy,
      },
    }));
    this.retrieveEntries();
  }

  onDateChange() {
    if (this.date && this.date[0] && this.date[1]) {
      this.changeTimeFrameFilter("custom_date");
      return true;
    }
    return false;
  }

  changeTimeFrameFilter(change: any) {
    let value = change;
    let label: string;
    if (value === "this_week") {
      label = "This week";
    }
    if (value === "today") {
      label = "Today";
    }
    if (value === "last_week") {
      label = "Last week";
    }
    if (value === "last_two_weeks") {
      label = "Last two weeks";
    }
    if (value === "last_four_weeks") {
      label = "Last four weeks";
    }
    if (value === "last_month") {
      label = "Last month";
    }
    if (value === "custom_date") {
      label = `${this.date[0].replace(/\./g, "-")} to ${this.date[1].replace(
        /\./g,
        "-",
      )}`;
      value = this.date;
    }
    this.date = "";
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState.timeTracking,
        timeFrameFilter: { label, value },
      },
    }));
    this.retrieveEntries();
  }

  changeGroupByFilter(change: string) {
    let label = change === "client" ? "Client" : "Employee";
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState.timeTracking,
        groupByFilter: { label, value: change },
      },
    }));
    this.changeSortByFilter("company", 1);
    this.retrieveEntries();
  }

  getTotalTimeTracked(data: string): number {
    let totalTime = 0;
    this.timeTrackings.forEach((entry) => {
      const key =
        this.dashboardService.filters()[this.dashboardName].groupByFilter
          .value === "employee"
          ? entry.userFullName
          : entry.company;
      if (key === data) {
        totalTime += entry.time_tracked;
      }
    });
    return totalTime;
  }

  async openJobPanel(jobUuid: any) {
    const _job = await this.jobsService.getJob(jobUuid);
    this.jobsStore.changeSelectedJob.next(_job);
  }

  onPageChange(event: any) {
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        rows: event.rows,
      },
    }));
  }

  exportPdf(): void {
    const {
      timeTracking: {
        personFilter: { value: personFilter },
        timeFrameFilter: { value: timeFrameFilter },
        groupByFilter: { value: groupByFilter },
        sortBy,
        sortOrder,
      },
    } = this.dashboardService.filters();
    this.timetrackingService
      .retrieveTimeTrackingEntries(
        personFilter,
        timeFrameFilter,
        sortBy,
        sortOrder,
      )
      .then(({ time_trackings }: any) => {
        import("jspdf").then((jsPDF) => {
          import("jspdf-autotable").then(() => {
            const doc: any = new jsPDF.default("l", "pt");
            doc.setProperties({
              title: "",
              subject: "",
              author: "",
              keywords: "",
              creator: "",
            });

            const exportCols = [
              groupByFilter === "client" ? "Client" : "Employee",
              groupByFilter === "client" ? "Employee" : "Client",
              "Job",
              "Start date",
              "Time tracked",
              "Notes",
            ];

            (jsPDF as any).autoTable(doc, {
              head: [exportCols],
              body: time_trackings.map((timeTracking: any) => {
                let employeeOrClient =
                  groupByFilter === "client"
                    ? timeTracking.company
                    : timeTracking.user.full_name;
                return [
                  employeeOrClient,
                  groupByFilter === "client"
                    ? timeTracking.user.full_name
                    : timeTracking.company,
                  timeTracking.job.name,
                  moment.utc(timeTracking.start_date).format("MMMM Do, YYYY"),
                  this.timetrackingService.timeTrackedFormatter(
                    timeTracking.time_tracked,
                  ),
                  timeTracking.notes ?? "-",
                ];
              }),
            });
            doc.save(
              `time_tracking_dashboard_${new Date()
                .toDateString()
                .replace(" ", "_")}.pdf`,
            );
          });
        });
      });
  }

  async exportExcel() {
    const {
      timeTracking: {
        personFilter: { value: personFilter },
        timeFrameFilter: { value: timeFrameFilter },
        sortBy,
        sortOrder,
      },
    } = this.dashboardService.filters();
    const { time_trackings }: any =
      await this.timetrackingService.retrieveTimeTrackingEntries(
        personFilter,
        timeFrameFilter,
        sortBy,
        sortOrder,
      );

    const timeTrackings = time_trackings;
    import("xlsx").then((xlsx) => {
      let formattedData = timeTrackings.map((timeTracking) => {
        return this.cols.reduce(
          (obj, col) => {
            let value = "";
            switch (col.field) {
              case "time_tracked":
                value = this.timetrackingService.timeTrackedFormatter(
                  timeTracking[col.field],
                );
                break;
              case "job":
                value = timeTracking[col.field]["name"];
                break;
              default:
                value = timeTracking[col.field];
            }
            obj[col.field] = value;
            return obj;
          },
          { employee: timeTracking.user.full_name },
        );
      });
      const wb = xlsx.utils.book_new();
      const ws = xlsx.utils.json_to_sheet([]);
      xlsx.utils.sheet_add_aoa(ws, [
        [
          "Employee",
          ...this.cols.map((col) =>
            col.field === "company" ? "Client" : col.header,
          ),
        ],
      ]);

      xlsx.utils.sheet_add_json(ws, formattedData, {
        origin: "A2",
        skipHeader: true,
      });

      xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
      xlsx.writeFile(
        wb,
        `time_tracking_dashboard${new Date().toDateString()}.xlsx`,
      );
    });
  }

  exportCsv(): void {
    const {
      timeTracking: {
        personFilter: { value: personFilter },
        timeFrameFilter: { value: timeFrameFilter },
        groupByFilter: { value: groupByFilter },
        sortBy,
        sortOrder,
      },
    } = this.dashboardService.filters();
    this.timetrackingService
      .retrieveTimeTrackingEntries(
        personFilter,
        timeFrameFilter,
        sortBy,
        sortOrder,
      )
      .then(({ time_trackings }: any) => {
        let csvContent = "data:text/csv;charset=utf-8,";
        const exportCols = [
          groupByFilter === "client" ? "Client" : "Employee",
          groupByFilter === "client" ? "Employee" : "Client",
          "Job",
          "Start date",
          "Time tracked",
          "Notes",
        ];
        csvContent += exportCols.join(",") + "\n";
        time_trackings.forEach((timeTracking: any) => {
          let employeeOrClient =
            groupByFilter === "client"
              ? timeTracking.company
              : timeTracking.user.full_name;
          let rowData = [
            employeeOrClient,
            groupByFilter === "client"
              ? timeTracking.user.full_name
              : timeTracking.company,
            timeTracking.job.name,
            moment.utc(timeTracking.start_date).format("MM-DD-YYYY"),
            this.timetrackingService.timeTrackedFormatter(
              timeTracking.time_tracked,
            ),
            timeTracking.notes ?? "-",
          ];
          csvContent += rowData.join(",") + "\n";
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute(
          "download",
          `time_tracking_dashboard_${new Date()
            .toDateString()
            .replace(" ", "_")}.csv`,
        );
        document.body.appendChild(link);
        link.click();
      });
  }

  updateTimeTracking(timeTrackingUuid: any, timeTracked: string) {
    const timeString = timeTracked.replace(/[^\d:]/g, "");
    let [hours, minutes, seconds] = timeString.split(":").map(Number);
    seconds = seconds || 0;
    if (hours && hours.toString().length > 2) {
      hours = Number(hours.toString().slice(0, 2));
    }
    if (minutes && minutes.toString().length > 2) {
      minutes = Number(minutes.toString().slice(0, 2));
    }
    if (seconds && seconds.toString().length > 2) {
      seconds = Number(seconds.toString().slice(0, 2));
    }
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    this.timetrackingService
      .updateTimeTrackingTime(timeTrackingUuid, totalSeconds)
      .then((data) => {
        const entryIndex = this.timeTrackings.findIndex(
          (entry) => entry.uuid == timeTrackingUuid,
        );
        this.timeTrackings[entryIndex].is_edited = true;
        this.timeTrackings[entryIndex].time_tracked = totalSeconds;
        this.getTotalTimeTracked(
          this.dashboardService.filters()[this.dashboardName].groupByFilter
            .value === "client"
            ? this.timeTrackings[entryIndex].company
            : this.timeTrackings[entryIndex].userFullName,
        );
        this.cdr.markForCheck();
        this.toastr.success("Time updated successfully!");
      });
  }

  updateTimeTrackingEnter(ev: KeyboardEvent, id: any, innerText: string) {
    if (ev.keyCode === 13) {
      ev.preventDefault();
      document.getElementById("editable-time-" + id)?.blur();
    }
  }
}
